import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';


const MediaGallery = ({ media, playerName }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const categories = [
    { id: 'all', label: 'All Media', icon: 'Grid' },
    { id: 'highlights', label: 'Match Highlights', icon: 'Video' },
    { id: 'training', label: 'Training Footage', icon: 'Activity' },
    { id: 'skills', label: 'Skill Demonstrations', icon: 'Target' },
    { id: 'photos', label: 'Professional Photos', icon: 'Camera' }
  ];

  const filteredMedia = activeCategory === 'all' 
    ? media 
    : media?.filter(item => item?.category === activeCategory);

  const openMediaModal = (mediaItem) => {
    setSelectedMedia(mediaItem);
    setIsModalOpen(true);
  };

  const closeMediaModal = () => {
    setSelectedMedia(null);
    setIsModalOpen(false);
  };

  if (!media || media?.length === 0) {
    return (
      <section className="py-12">
        <h2 className="text-3xl font-bold text-foreground font-headline mb-8">Media Gallery</h2>
        <div className="bg-muted/50 border border-border rounded-lg p-12 text-center">
          <Icon name="Image" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No media available for this player yet.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-foreground font-headline">Media Gallery</h2>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon name="Film" size={20} />
          <span className="text-sm">{filteredMedia?.length} items</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mb-8">
        {categories?.map(category => (
          <button
            key={category?.id}
            onClick={() => setActiveCategory(category?.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeCategory === category?.id
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'bg-card border border-border text-foreground hover:bg-muted'
            }`}
          >
            <Icon name={category?.icon} size={18} />
            {category?.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMedia?.map((item, index) => (
          <div
            key={item?.id || index}
            className="group relative bg-card border border-border rounded-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300"
            onClick={() => openMediaModal(item)}
          >
            <div className="aspect-video relative overflow-hidden bg-muted">
              {item?.mediaType === 'video' ? (
                <>
                  <video
                    src={item?.mediaUrl}
                    className="w-full h-full object-cover"
                    preload="metadata"
                  />
                  <div className="absolute inset-0 bg-background/50 flex items-center justify-center group-hover:bg-background/30 transition-colors">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="Play" size={32} className="text-primary-foreground ml-1" />
                    </div>
                  </div>
                </>
              ) : (
                <Image
                  src={item?.mediaUrl}
                  alt={item?.caption || `${playerName} media item`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-primary/10 text-primary px-2 py-1 rounded text-xs font-medium capitalize">
                  {item?.category || 'General'}
                </div>
                {item?.mediaType === 'video' && item?.duration && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Clock" size={12} />
                    {item?.duration}
                  </div>
                )}
              </div>
              <p className="text-sm text-foreground font-medium line-clamp-2">
                {item?.caption || 'Untitled media'}
              </p>
            </div>
          </div>
        ))}
      </div>
      {isModalOpen && selectedMedia && (
        <div className="fixed inset-0 z-50 bg-background/95 flex items-center justify-center p-4" onClick={closeMediaModal}>
          <div className="relative max-w-5xl w-full bg-card border border-border rounded-xl overflow-hidden" onClick={(e) => e?.stopPropagation()}>
            <button
              onClick={closeMediaModal}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-background/80 hover:bg-background rounded-full flex items-center justify-center transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
            
            <div className="aspect-video bg-background">
              {selectedMedia?.mediaType === 'video' ? (
                <video
                  src={selectedMedia?.mediaUrl}
                  controls
                  autoPlay
                  className="w-full h-full"
                />
              ) : (
                <Image
                  src={selectedMedia?.mediaUrl}
                  alt={selectedMedia?.caption || `${playerName} media`}
                  className="w-full h-full object-contain"
                />
              )}
            </div>
            
            <div className="p-6 border-t border-border">
              <div className="flex items-center gap-2 mb-3">
                <div className="bg-primary/10 text-primary px-3 py-1 rounded-md text-sm font-medium capitalize">
                  {selectedMedia?.category || 'General'}
                </div>
              </div>
              <p className="text-foreground font-medium mb-2">{selectedMedia?.caption || 'Untitled media'}</p>
              {selectedMedia?.description && (
                <p className="text-sm text-muted-foreground">{selectedMedia?.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MediaGallery;